import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { VoucherGroup, Order, TownshipPublicSettings } from 'src/app/interfaces';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { httpsCallable } from 'firebase/functions';
import { functions } from 'src/app/app.component';
import { updateTheme } from 'src/app/globals';
import { PublicFunctionsService } from 'src/app/services/public-functions.service';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatDivider } from '@angular/material/list';
import { MatButton } from '@angular/material/button';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-buy-success',
  templateUrl: './buy-success.component.html',
  styleUrls: ['./buy-success.component.scss'],
  imports: [MatProgressSpinner, MatDivider, MatButton, TranslatePipe],
})
export class BuySuccessComponent implements OnInit, OnDestroy {
  readonly functions = functions;
  env = environment;
  redirectUrl = signal<string>('');
  order = signal<Order | null>(null);
  titleAbove = signal<string>('');
  titleBelow = signal<string>('');

  awaitingResponse = signal(true);

  displayedColumns: string[] = ['Artikel', 'Aantal', 'Prijs'];
  columnsToDisplay: string[] = this.displayedColumns.slice();
  data = signal<any[]>([]);

  failed = signal(false);
  private readonly subscriptions = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private translate: TranslateService,
    private publicFunctionsService: PublicFunctionsService,
  ) {}

  async ngOnInit() {
    this.translate.setDefaultLang(environment.language);
    this.translate.use(environment.language);
    this.subscriptions.add(
      this.route.params.subscribe(async (params) => {
        const orderData = {
          voucherGroupdId: params.voucherGroupdId,
          townshipId: params.townshipId,
          orderId: params.orderId,
        };
        await this.checkOrderStatus(orderData, 3);
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  async checkOrderStatus(orderData: any, attemptsLeft: number) {
    const callable = httpsCallable(this.functions, 'httpGetOrder');
    try {
      const recaptchaToken = await this.publicFunctionsService.getRecaptchaToken('get_order');
      const result = await callable({
        ...orderData,
        recaptchaToken,
      });
      if (result.data) {
        const voucherGroup = result.data['voucherGroup'] as VoucherGroup;
        const order = result.data['order'] as Order;
        const township = result.data['township'] as TownshipPublicSettings;

        this.order.set(order);
        updateTheme({
          primary: township.primaryColor,
          secondary: township.accentColor,
        });
        if (voucherGroup.redirectUrl) {
          this.redirectUrl.set(voucherGroup.redirectUrl);
        }

        const isPending = order.status === 'reserved' || order.status === 'open';
        const isGeneratingInvoice =
          order.type === 'digital' &&
          (order.status === 'paid' || order.status === 'done') &&
          !order.downloadUrl;

        // If the order status is still reserved/open, or if it is a digital order that is paid/done but the expected downloadUrl is still generating, wait 3 seconds and retry
        if ((isPending || isGeneratingInvoice) && attemptsLeft > 0) {
          setTimeout(() => this.checkOrderStatus(orderData, attemptsLeft - 1), 3000);
          return;
        }

        let titleAbove = '';
        let titleBelow = '';
        let tableArticleVoucher = '';
        let tableArticleCosts = '';
        let tableArticleTotal = '';

        switch (order.status) {
          case 'done':
          case 'paid':
            titleAbove = await this.getTranslation('succeed.message-above');
            titleBelow = await this.getTranslation('succeed.message-under');
            tableArticleVoucher = await this.getTranslation('succeed.article-voucher');
            tableArticleCosts = await this.getTranslation('succeed.article-costs');
            tableArticleTotal = await this.getTranslation('succeed.article-total');
            let envName = township.voucherPrefix;
            envName = envName.charAt(0).toUpperCase() + envName.slice(1);
            const tableData = [
              {
                article: `${envName} ${tableArticleVoucher} €${order.price}`,
                amount: `${order.amount}x`,
                price: `	€${this.getCurrencyString(order.price * order.amount)}`,
              },
            ];
            tableData.push({
              article: `${tableArticleCosts}`,
              amount: `1x`,
              price: `	€${this.getCurrencyString(order.administrationCost)}`,
            });
            if (order.useGiftWrapping) {
              const giftWrappingLabel = await this.getTranslation('step1.gift-wrapping');
              tableData.push({
                article: giftWrappingLabel,
                amount: `${order.amount}x`,
                price: `	€${this.getCurrencyString(order.giftWrappingPrice * order.amount)}`,
              });
            }
            tableData.push({
              article: `${tableArticleTotal}`,
              amount: ``,
              price: `	€${this.getCurrencyString(order.totalPrice)}`,
            });
            this.data.set(tableData);
            break;
          case 'open':
            if (voucherGroup.paymentProvider === 'ideal') {
              titleAbove = await this.getTranslation('processing.message-above-ideal');
              titleBelow = await this.getTranslation('processing.message-under-ideal');
            } else {
              titleAbove = await this.getTranslation('processing.message-above');
              titleBelow = await this.getTranslation('processing.message-under');
            }
            break;
          case 'canceled':
            titleAbove = await this.getTranslation('cancelled.message-above');
            titleBelow = await this.getTranslation('cancelled.message-under');
            break;
          case 'expired':
            titleAbove = await this.getTranslation('expired.message-above');
            titleBelow = await this.getTranslation('expired.message-under');
            break;
          case 'failed':
            titleAbove = await this.getTranslation('failed.message-above');
            titleBelow = await this.getTranslation('failed.message-under');
            break;
          default:
            titleAbove = await this.getTranslation('failed.message-above');
            titleBelow = await this.getTranslation('failed.message-under');
            break;
        }
        this.titleAbove.set(titleAbove);
        this.titleBelow.set(titleBelow);
        this.awaitingResponse.set(false);
      } else {
        this.failed.set(true);
        this.awaitingResponse.set(false);
      }
    } catch {
      if (attemptsLeft > 0) {
        setTimeout(() => this.checkOrderStatus(orderData, attemptsLeft - 1), 3000);
      } else {
        this.failed.set(true);
        this.awaitingResponse.set(false);
      }
    }
  }

  openWindow(url: string) {
    window.open(url, '_blank');
  }

  getTranslation(objName): any {
    const res = this.translate.instant(`buy.${objName}`);
    return res;
  }

  getCurrencyString(number: any) {
    if (!number) {
      number = 0;
    }
    let newNumber;
    if (!number.toString().includes('.')) {
      // this isnt a decimal
      newNumber = `${number.toString()},-`;
    } else {
      newNumber = `${number.toFixed(2)}`;
      newNumber = newNumber.replace('.', ',');
    }
    return newNumber;
  }
}
